import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileText, Loader2, Download, Copy, Printer, FileDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import html2pdf from "html2pdf.js";

// Reusable prose renderer for AI markdown outputs
export const AiMarkdown = ({ text, testid }) => (
  <div data-testid={testid}
    className="prose prose-sm dark:prose-invert max-w-none
               prose-headings:font-heading prose-headings:tracking-tight
               prose-h1:font-display prose-h1:text-3xl prose-h1:mb-2
               prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2 prose-h2:pb-1 prose-h2:border-b prose-h2:border-border
               prose-h3:text-base
               prose-p:leading-relaxed
               prose-strong:text-foreground
               prose-a:text-primary
               prose-table:text-xs prose-table:border prose-table:border-border
               prose-th:bg-muted prose-th:px-2 prose-th:py-1.5 prose-th:text-left prose-th:font-medium
               prose-td:px-2 prose-td:py-1.5 prose-td:border-t prose-td:border-border
               prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5
               prose-hr:my-4">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

export default function DailyReports() {
  const [form, setForm] = useState({
    project_id: "",
    project_name: "",
    labor: 120,
    equipment: "excavator, tower crane, concrete pump",
    materials: "OPC 50 bags, TMT rebar 2 t, aggregate 20 m³",
    weather: "28°C · partly cloudy · light drizzle from 3pm",
    activities: "pier foundation pouring, drainage backfilling",
    incidents: "",
  });
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => { apiClient().get("/ai/dprs").then((r) => setItems(r.data)); }, []);

  const generate = async () => {
    setBusy(true); setReport("");
    try {
      const payload = {
        project_id: form.project_id || "default",
        labor: Number(form.labor) || 0,
        equipment: form.equipment.split(",").map(s => s.trim()).filter(Boolean),
        materials: form.materials.split(",").map(s => s.trim()).filter(Boolean),
        weather: form.weather,
        activities: form.activities.split(",").map(s => s.trim()).filter(Boolean),
        incidents: form.incidents.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (form.project_name) payload.project_name = form.project_name;
      const { data } = await apiClient().post("/ai/dpr", payload);
      setReport(data.report);
      toast.success("DPR generated");
      const list = await apiClient().get("/ai/dprs");
      setItems(list.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(report);
    toast.success("Copied to clipboard");
  };
  const download = () => {
    const blob = new Blob([report], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dpr-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const downloadPdf = () => {
    const el = document.getElementById("dpr-print-area");
    if (!el) return;
    toast.info("Preparing PDF…");
    const opts = {
      margin: [12, 12, 14, 12],
      filename: `setu-dpr-${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };
    // Force light mode for the PDF render
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "background:#ffffff;color:#111;padding:24px;font-family:'Geist',sans-serif;max-width:800px;";
    wrapper.innerHTML = `
      <div style="border-bottom:2px solid #B54426;padding-bottom:12px;margin-bottom:18px;display:flex;align-items:center;justify-content:space-between;">
        <div>
          <div style="font-family:'Instrument Serif',serif;font-size:28px;color:#B54426;line-height:1;">Setubuild.OS</div>
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.15em;color:#666;margin-top:4px;">Daily Progress Report</div>
        </div>
        <div style="font-size:10px;color:#666;">${new Date().toLocaleString()}</div>
      </div>
      ${el.innerHTML}
      <div style="margin-top:36px;padding-top:12px;border-top:1px solid #ddd;font-size:9px;color:#888;text-align:center;">
        Generated by Setubuild.OS · The Bridge between blueprint and building
      </div>`;
    html2pdf().set(opts).from(wrapper).save().then(() => toast.success("PDF downloaded"));
  };
  const print = () => window.print();

  return (
    <div className="space-y-6 fade-in-up" data-testid="reports-page">
      <div>
        <h1 className="font-display text-5xl mt-1">Daily Progress Reports</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Fill the day's facts, get a professionally-structured DPR — ready to email, print, or archive.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 border border-border bg-card p-5 space-y-3 rounded-lg">
          <div className="font-heading text-lg">Today's inputs</div>
          {[
            ["project_name", "Project name", "input"],
            ["labor", "Labor count", "input"],
            ["equipment", "Equipment (comma-separated)", "input"],
            ["materials", "Materials used", "textarea"],
            ["weather", "Weather", "input"],
            ["activities", "Activities completed", "textarea"],
            ["incidents", "Incidents (optional)", "textarea"],
          ].map(([k, l, kind]) => (
            <div key={k}>
              <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</label>
              {kind === "textarea" ? (
                <textarea rows={2} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`report-input-${k}`}
                  className="w-full mt-1 border border-border bg-background px-3 py-2 text-sm rounded focus:outline-none focus:border-primary" />
              ) : (
                <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`report-input-${k}`}
                  className="w-full mt-1 border border-border bg-background px-3 py-2 text-sm rounded focus:outline-none focus:border-primary" />
              )}
            </div>
          ))}
          <button onClick={generate} disabled={busy} data-testid="report-generate-btn"
            className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 font-medium disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {busy ? "Generating…" : "Generate DPR"}
          </button>
        </div>

        <div className="lg:col-span-3 border border-border bg-card p-6 rounded-lg print:border-0 print:p-0">
          <div className="flex items-center justify-between mb-4 print:hidden">
            <div className="font-heading text-lg">Report</div>
            {report && (
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={copy} data-testid="report-copy" className="btn-sharp inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:bg-muted"><Copy className="h-3.5 w-3.5" /> Copy</button>
                <button onClick={download} data-testid="report-download" className="btn-sharp inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:bg-muted"><Download className="h-3.5 w-3.5" /> .md</button>
                <button onClick={downloadPdf} data-testid="report-pdf" className="btn-sharp inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 text-xs"><FileDown className="h-3.5 w-3.5" /> PDF</button>
                <button onClick={print} data-testid="report-print" className="btn-sharp inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs hover:bg-muted"><Printer className="h-3.5 w-3.5" /> Print</button>
              </div>
            )}
          </div>
          {report ? (
            <div id="dpr-print-area">
              <AiMarkdown text={report} testid="report-output" />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-12 text-center">
              Fill the inputs and click <span className="mono text-primary">Generate DPR</span>. Reports appear here in a printable, share-ready format.
            </div>
          )}
        </div>
      </div>

      <div className="border border-border bg-card p-5 rounded-lg print:hidden">
        <div className="font-heading text-lg mb-3">Report History</div>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No past reports.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((r) => (
              <li key={r.id} className="border border-border p-3 text-sm rounded" data-testid={`report-item-${r.id}`}>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                <div className="mt-1 line-clamp-2 text-muted-foreground">{(r.report || "").replace(/[#*_`|-]/g, "").slice(0, 200)}…</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
